/**
 * @fileoverview React hook for managing event card actions
 *
 * Provides a clean interface for components to use the action system,
 * including primary CTA calculation, secondary actions, and action handlers.
 *
 * @author LOCI Development Team
 * @since 1.0.0
 */
import { useMemo, useCallback, useState, useEffect } from 'react';
import { calculatePrimaryCTA, getSecondaryActions, handleActionClick, } from '../utils/actionManager';
import { openCalendarProvider, detectUserPreference } from '../utils/calendarIntegration';
/**
 * Detect current platform based on viewport width
 */
function detectPlatform(width) {
    if (width < 768) {
        return 'mobile';
    }
    else if (width < 1024) {
        return 'tablet';
    }
    return 'desktop';
}
/**
 * Get current viewport width
 */
function getViewportWidth() {
    if (typeof window === 'undefined') {
        return 1920; // SSR default to desktop
    }
    return window.innerWidth;
}
/**
 * React hook for managing event card actions
 *
 * @example
 * ```tsx
 * function EventCard({ event }) {
 *   const { primaryCTA, secondaryActions, handlePrimaryClick } = useEventActions({
 *     event,
 *     user: currentUser,
 *   });
 *
 *   return (
 *     <div>
 *       <button onClick={handlePrimaryClick}>
 *         {primaryCTA.label}
 *       </button>
 *       {secondaryActions.map(action => (
 *         <button key={action.type} onClick={() => handleSecondaryClick(action.type)}>
 *           {action.label}
 *         </button>
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
export function useEventActions(options) {
    const { event, user = null, platform: platformOverride, viewportWidth: viewportWidthOverride, onActionClick, onCalendarClick, } = options;
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    // Auto-reset success state after 3 seconds
    useEffect(() => {
        if (success) {
            const timer = setTimeout(() => {
                setSuccess(false);
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [success]);
    // Detect display context
    const displayContext = useMemo(() => {
        const width = viewportWidthOverride ?? getViewportWidth();
        const platform = platformOverride ?? detectPlatform(width);
        return {
            platform,
            viewportWidth: width,
        };
    }, [platformOverride, viewportWidthOverride]);
    // Calculate primary CTA
    const primaryCTA = useMemo(() => calculatePrimaryCTA(event, user, displayContext), [event, user, displayContext]);
    // Get secondary actions
    const secondaryActions = useMemo(() => getSecondaryActions(event, user, displayContext), [event, user, displayContext]);
    // Handle primary action click
    const handlePrimaryClick = useCallback(async () => {
        if (primaryCTA.disabled) {
            return;
        }
        setIsLoading(true);
        setError(null);
        setSuccess(false);
        try {
            // Notify parent component
            if (onActionClick) {
                onActionClick(primaryCTA.type);
            }
            // Handle the action
            const result = await handleActionClick(primaryCTA.type, event, user, displayContext);
            if (result.success) {
                setSuccess(true);
            }
            else {
                setError(result.message || 'Action failed');
            }
        }
        catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
        }
        finally {
            setIsLoading(false);
        }
    }, [primaryCTA, event, user, displayContext, onActionClick]);
    // Handle secondary action click
    const handleSecondaryClick = useCallback(async (action) => {
        setIsLoading(true);
        setError(null);
        setSuccess(false);
        try {
            // Notify parent component
            if (onActionClick) {
                onActionClick(action);
            }
            // Handle the action
            const result = await handleActionClick(action, event, user, displayContext);
            if (result.success) {
                setSuccess(true);
            }
            else {
                setError(result.message || 'Action failed');
            }
        }
        catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
        }
        finally {
            setIsLoading(false);
        }
    }, [event, user, displayContext, onActionClick]);
    // Handle calendar action click
    const handleCalendarClick = useCallback((provider) => {
        const selectedProvider = provider ?? detectUserPreference();
        // Notify parent component
        if (onCalendarClick) {
            onCalendarClick(selectedProvider);
        }
        // Open calendar provider
        openCalendarProvider(event, selectedProvider);
    }, [event, onCalendarClick]);
    return {
        primaryCTA,
        secondaryActions,
        handlePrimaryClick,
        handleSecondaryClick,
        handleCalendarClick,
        isLoading,
        error,
        success,
    };
}
